import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GlobalReactQueryOptions } from '../../config';
import { CopiesListOut, LineItemCopyOut, LineItemKey } from '../../store/api';
import { browserClient } from '../../react-query';

type Options = {
    readonly lineItemKey: LineItemKey;
    readonly fetchFunc: (productId: string) => Promise<CopiesListOut>;
};

const LINE_ITEM_COPY_DETAILS_KEY = 'getLineItemCopyDetails';

const EMPTY_RESPONSE = { copies: [] };

export const useLineItemCopyDetails = ({ lineItemKey, fetchFunc }: Options) => {
    const { data: { copies } = EMPTY_RESPONSE, isLoading } = useQuery(
        [LINE_ITEM_COPY_DETAILS_KEY, lineItemKey],
        () => fetchFunc(lineItemKey.productId),
        {
            ...GlobalReactQueryOptions
        }
    );

    const filteredCopies = useMemo(() => copies.filter(isValidLineItemKey(lineItemKey)), [copies, lineItemKey]);

    return {
        isLoading: isLoading,
        copies: filteredCopies
    };
};

function isValidLineItemKey(lineItemKey: LineItemKey) {
    if (!lineItemKey?.usageCondition) {
        return () => true;
    }

    return ({ price = {}, subscriptionPeriodType }: LineItemCopyOut) => {
        if (lineItemKey.subscriptionPeriodType) {
            return (
                lineItemKey.subscriptionPeriodType === subscriptionPeriodType &&
                lineItemKey.usageCondition?.lendingModel === price.lendingModel &&
                lineItemKey.usageCondition?.usageLimit === price.usageLimit &&
                lineItemKey.usageCondition?.termLimit === price.termLimit
            );
        }
        return (
            lineItemKey.usageCondition?.lendingModel === price.lendingModel &&
            lineItemKey.usageCondition?.usageLimit === price.usageLimit &&
            lineItemKey.usageCondition?.termLimit === price.termLimit
        );
    };
}

export const invalidateLineItemCopies = (lineItemKey: LineItemKey) => {
    return browserClient.invalidateQueries({ queryKey: [LINE_ITEM_COPY_DETAILS_KEY, lineItemKey] });
};
